/**
 * Outreach API (Gluska-Forge-API, /api/outreach). Personal plane and
 * flag-only: none of these calls depend on the active license, and a user
 * without `outreach_enabled` gets 403 everywhere. apiClient methods already
 * return the response body; the `{data}` envelope is unwrapped here.
 */
import { apiClient } from "@/lib/api"
import type {
  ChecklistItem,
  InboxThread,
  InboxView,
  GmailStatus,
  HiringFilter,
  OutreachAction,
  OutreachList,
  OutreachProspect,
  OutreachRun,
  OutreachRunDetail,
  OutreachSettings,
  OutreachSettingsValues,
  OutreachStats,
  RunKind,
  RunStatus,
  RunsSummary,
  RaiseWindow,
  ReplyChannel,
  SeoStrength,
  TodayGroups,
} from "@/lib/outreach"

export interface OutreachFilters {
  list_type?: OutreachList | ""
  q?: string
  raise_window?: RaiseWindow | ""
  seo_strength?: SeoStrength | "unknown" | ""
  /** any = an open YC post for a growth/marketing/content/SEO role; seo = one that mentions SEO/GEO. */
  hiring?: HiringFilter | ""
  /** One YC batch, exactly as stored ("Spring 2026"); the Backlog tab's batch chips set it. */
  yc_batch?: string
  /** One source the company was found in (yc, form_d, news, techstars…); the Backlog tab's source chips set it. */
  source?: string
}

export interface OutreachListParams extends OutreachFilters {
  /** One stage, a comma list, or `linkedin` for both LinkedIn stages. */
  stage?: string
  /** With stage=approved: "1" lists the held ones (research parked), "0" the ones still being researched. */
  research_held?: "0" | "1"
  /** best: the pipeline's backlog_score, highest first (the Backlog tab). */
  sort?: "newest" | "oldest" | "days_since_raise" | "-days_since_raise" | "review" | "best"
  per_page?: number
  page?: number
}

export interface OutreachPage {
  data: OutreachProspect[]
  meta: { current_page: number; last_page: number; per_page: number; total: number }
}

export interface OutreachInboxPage {
  data: InboxThread[]
  counts: { all: number; unread: number }
  meta: { current_page: number; last_page: number; per_page: number; total: number }
}

export interface OutreachRunFilters {
  kind?: RunKind | ""
  status?: RunStatus | ""
  page?: number
  per_page?: number
}

export interface OutreachRunsPage {
  data: OutreachRun[]
  meta: { current_page: number; last_page: number; per_page: number; total: number }
}

export type OutreachSettingsBody = Partial<OutreachSettingsValues> & { checklist?: Pick<ChecklistItem, "key" | "label" | "detail" | "done">[] }

export type OutreachActionBody = {
  reason?: string
  channel?: ReplyChannel
  text?: string | null
  note?: string | null
  /** schedule_send: the updated_at of the draft the person read; the API refuses a draft changed since. */
  draft_updated_at?: string | null
}

/** Drop empty filters so the query key and the URL stay the same for "no filter". */
export function cleanParams<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v !== undefined && v !== null)) as Partial<T>
}

export const outreachApi = {
  stats: (filters: OutreachFilters = {}) =>
    apiClient.get<{ data: OutreachStats }>("/outreach/stats", { params: cleanParams(filters) }).then((r) => r.data),
  today: (filters: OutreachFilters = {}) =>
    apiClient.get<{ date: string; groups: TodayGroups }>("/outreach/today", { params: cleanParams(filters) }),
  list: (params: OutreachListParams) => apiClient.get<OutreachPage>("/outreach/prospects", { params: cleanParams(params) }),
  get: (id: number) => apiClient.get<{ data: OutreachProspect }>(`/outreach/prospects/${id}`).then((r) => r.data),
  update: (id: number, body: Partial<OutreachProspect>) =>
    apiClient.patch<{ data: OutreachProspect }>(`/outreach/prospects/${id}`, body).then((r) => r.data),
  action: (id: number, action: OutreachAction, body: OutreachActionBody = {}) =>
    apiClient.post<{ data: OutreachProspect }>(`/outreach/prospects/${id}/actions/${action}`, body).then((r) => r.data),
  runs: (filters: OutreachRunFilters = {}) => apiClient.get<OutreachRunsPage>("/outreach/runs", { params: cleanParams(filters) }),
  run: (id: number) => apiClient.get<{ data: OutreachRunDetail }>(`/outreach/runs/${id}`).then((r) => r.data),
  runsSummary: () => apiClient.get<{ data: RunsSummary }>("/outreach/runs/summary").then((r) => r.data),
  settings: () => apiClient.get<{ data: OutreachSettings }>("/outreach/settings").then((r) => r.data),
  saveSettings: (body: OutreachSettingsBody) => apiClient.put<{ data: OutreachSettings }>("/outreach/settings", body).then((r) => r.data),
  setChecklistItem: (key: string, done: boolean) =>
    apiClient.patch<{ data: OutreachSettings }>(`/outreach/settings/checklist/${key}`, { done }).then((r) => r.data),
  /** Every Gmail thread Forge started, unread first. */
  inbox: (params: { view?: InboxView; q?: string; page?: number; per_page?: number }) =>
    apiClient.get<OutreachInboxPage>("/outreach/inbox", { params: cleanParams(params) }),
  gmail: () => apiClient.get<{ data: GmailStatus }>("/outreach/gmail").then((r) => r.data),
  /** Google's consent URL for the sender address; the browser goes there and comes back to /outreach/settings. */
  gmailAuthUrl: () => apiClient.get<{ auth_url: string }>("/outreach/gmail/auth-url").then((r) => r.auth_url),
  disconnectGmail: () => apiClient.delete<{ data: GmailStatus }>("/outreach/gmail").then((r) => r.data),
  /** A reply or follow-up in the prospect's Gmail thread, sent after the undo window. */
  sendMessage: (id: number, body: string) =>
    apiClient.post<{ data: OutreachProspect }>(`/outreach/prospects/${id}/messages`, { body }).then((r) => r.data),
  /** Cancel an email that has not gone yet; a first email takes the prospect back to drafted. */
  cancelMessage: (messageId: number) => apiClient.delete<{ data: OutreachProspect }>(`/outreach/messages/${messageId}`).then((r) => r.data),
  bulk: (action: OutreachAction, ids: number[], body: OutreachActionBody = {}) =>
    apiClient.post<{ done: number[]; failed: { id: number; message: string }[] }>(`/outreach/prospects/bulk-actions/${action}`, { ids, ...body }),
}
